import { KButton, KTable, KIcon } from "@kyburz/kdesign-system";
import { makeStyles } from "@material-ui/core/styles";
import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setAlert } from "../../redux/actions/actions";
import {
  getVehicleTypes,
  deleteVehicleTypes,
} from "../../apiCalls/admin/vehicleTypes";
import useSetState from "../../hooks/useSetState";
import NewVehicleType from "./NewVehicleType";
import EditVehicleType from "./EditVehicleType";
import { RootStateInterface } from "../../interfaces/ReduxInterface";

const MemoizedKTable = React.memo(
  (props: any) => (
    <KTable
      data={props.data}
      columns={props.columns}
      tableHeight="calc(100% - 100px)"
    />
  ),
  (oldProps, newProps) => {
    return oldProps.data === newProps.data;
  },
);

const useStyles = makeStyles(() => ({
  ctaContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  tableContainer: {
    maxHeight: "50vh",
  },
  iconContainer: {
    display: "inline-block",
    cursor: "pointer",
  },
}));

const VehicleTypes = () => {
  const currentCustomer = useSelector(
    (state: RootStateInterface) => state?.customer?.currentCustomer,
  );
  const dispatch = useDispatch();
  const classes = useStyles();
  const [
    {
      items,
      newTypeOpen,
      reload,
      openEdit,
      vehicleType,
      skeletonUrl,
      icon_3dUrl,
      icon_2dUrl,
      label,
    },
    setState,
  ] = useSetState({
    items: [],
    newTypeOpen: false,
    reload: true,
    openEdit: false,
    vehicleType: "",
    skeletonUrl: "",
    icon_3dUrl: "",
    icon_2dUrl: "",
    label: { en: "", de: "", fr: "", it: "" },
  });

  const toggleNewTypePopUp = (withReload = false): void => {
    setState({ newTypeOpen: !newTypeOpen, reload: withReload });
  };

  const loadVehicleTypes = (): void => {
    getVehicleTypes(currentCustomer?.shortname)
      .then((res) => {
        setState({
          items: res.data?.data.map((x) => ({
            name: x.name,
            skeleton: x.skeleton,
            icon_3d: x.icon_3d,
            icon_2d: x.icon_2d,
            label: x.label,
          })),
        });
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const removeVehicleType = async (vehicleTypeName) => {
    const res = await deleteVehicleTypes(
      currentCustomer?.shortname,
      vehicleTypeName,
    );
    if (res?.data) {
      setState({ reload: true });
      dispatch(setAlert(`Vehicle type ${vehicleTypeName} deleted`, "success"));
    } else {
      dispatch(setAlert(res?.detail?.detail, "error"));
    }
  };

  const handleModal = (type) => {
    if (openEdit === true) {
      setState({ reload: true });
    }
    setState({
      openEdit: !openEdit,
      vehicleType: type.name,
      skeletonUrl: type.skeleton,
      icon_3dUrl: type.icon_3d,
      icon_2dUrl: type.icon_2d,
      label: type.label,
    });
  };

  useEffect(() => {
    if (reload) {
      setState({ reload: false });
      loadVehicleTypes();
    }
  }, [reload]);

  const columns = React.useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        defaultCanSort: true,
      },
      {
        Header: "Skeleton",
        accessor: "skeleton",
        defaultCanSort: true,
      },
      {
        Header: "Icon 3D",
        accessor: "icon_3d",
        defaultCanSort: true,
      },
      {
        Header: "Icon 2D",
        accessor: "icon_2d",
        defaultCanSort: true,
      },
      {
        accessor: "actions",
        Cell: (row) => (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginRight: 16,
            }}
          >
            <div
              className={classes.iconContainer}
              onClick={() => handleModal(row.row.original)}
              style={{ marginRight: "16px" }}
            >
              <KIcon icon="edit" color="#383F51" size={20} />
            </div>
            <div
              className={classes.iconContainer}
              onClick={() => removeVehicleType(row.row.original.name)}
            >
              <KIcon icon="trash-2" color="#383F51" size={20} />
            </div>
          </div>
        ),
        width: 100,
      },
    ],
    [],
  );

  const newTypePopUp: React.ReactNode = (
    <NewVehicleType open={newTypeOpen} handleClose={toggleNewTypePopUp} />
  );

  return (
    <>
      <div className={classes.ctaContainer}>
        {openEdit && (
          <EditVehicleType
            open={openEdit}
            handleClose={handleModal}
            vehicleType={vehicleType}
            skeletonUrl={skeletonUrl}
            icon_3dUrl={icon_3dUrl}
            icon_2dUrl={icon_2dUrl}
            label={label}
          />
        )}
        <div style={{ flexGrow: 1 }} />
        <KButton
          children="Add Type"
          icon={<KIcon icon="plus" />}
          onClick={() => toggleNewTypePopUp()}
        />
      </div>
      <MemoizedKTable columns={columns} data={items} />
      {newTypePopUp}
    </>
  );
};

export default VehicleTypes;
